import type { api } from "@healthos/schemas";
import { createContext, type ReactNode, useContext } from "react";

type CommunityProfileTrigger = {
  currentUserId: string | null;
  onOpenProfile: (userId: string) => void;
};

const CommunityProfileTriggerContext = createContext<CommunityProfileTrigger | null>(null);

export function CommunityProfileTriggerProvider({
  currentUserId,
  onOpenProfile,
  children,
}: CommunityProfileTrigger & {
  children: ReactNode;
}) {
  return (
    <CommunityProfileTriggerContext value={{ currentUserId, onOpenProfile }}>
      {children}
    </CommunityProfileTriggerContext>
  );
}

export function useCommunityProfileTrigger(
  author: Pick<api.CommunityAuthor, "displayName"> & { userId?: string },
): (() => void) | null {
  const trigger = useContext(CommunityProfileTriggerContext);
  const userId = author.userId;
  if (!trigger || !userId || trigger.currentUserId === userId) return null;
  return () => trigger.onOpenProfile(userId);
}

/**
 * Whether this author is the member reading the feed.
 *
 * Feed copy is written in the third person ("completed their first weekly
 * reflection"), which is right for a teammate and wrong for the reader, whose
 * name renders as "You": "You completed their first weekly reflection". The
 * answer comes from the id rather than from testing the display name, because
 * a member actually called "You" would otherwise decide their own grammar.
 */
export function useIsSelf(author: { userId?: string }): boolean {
  const trigger = useContext(CommunityProfileTriggerContext);
  return Boolean(trigger?.currentUserId && author.userId === trigger.currentUserId);
}
